/**
 * ============================================================================
 * NOTIFICATION ITEM COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Individual notification item with actions
 */

import React from 'react';
import { useI18n } from '../../providers';
import Avatar from '../common/Avatar';
import StatusBadge from '../common/StatusBadge';

/**
 * Notification Item Component
 * @param {Object} props
 * @param {Object} props.notification - Notification object
 * @param {Function} props.onClick - Click handler
 * @param {Function} props.onDelete - Delete handler
 * @param {Function} props.onMarkRead - Mark as read handler
 * @param {boolean} props.compact - Compact mode
 * @returns {JSX.Element}
 */
const NotificationItem = ({
  notification,
  onClick,
  onDelete,
  onMarkRead,
  compact = false,
}) => {
  const { formatDate, formatTime } = useI18n();

  const getNotificationIcon = (type) => {
    const icons = {
      payment: '💰',
      delivery: '📦',
      dispute: '⚠️',
      message: '💬',
      system: '🔔',
      verification: '✅',
      withdrawal: '💸',
      transaction: '📊',
      offer: '💼',
      mediation: '⚖️',
    };
    return icons[type] || '🔔';
  };

  const getNotificationColor = (type, priority = 'normal') => {
    const priorityColors = {
      urgent: 'bg-red-500',
      high: 'bg-orange-500',
      normal: 'bg-blue-500',
      low: 'bg-gray-500',
    };

    const typeColors = {
      payment: 'bg-blue-500',
      delivery: 'bg-green-500',
      dispute: 'bg-red-500',
      message: 'bg-purple-500',
      system: 'bg-gray-500',
      verification: 'bg-green-500',
      withdrawal: 'bg-yellow-500',
      transaction: 'bg-cyan-500',
      offer: 'bg-indigo-500',
      mediation: 'bg-pink-500',
    };

    return priorityColors[priority] || typeColors[type] || 'bg-blue-500';
  };

  const handleClick = () => {
    if (onClick) onClick(notification);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(notification.id);
  };

  const handleMarkRead = (e) => {
    e.stopPropagation();
    if (onMarkRead) onMarkRead(notification.id);
  };

  const isRead = notification.isRead || notification.read || false;
  const priority = notification.priority || 'normal';
  const iconColor = getNotificationColor(notification.type, priority);

  return (
    <div
      className={`
        group relative
        p-4 rounded-xl
        border-2 transition-all duration-200
        cursor-pointer
        transform hover:scale-[1.02] hover:shadow-lg
        ${
          isRead
            ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            : 'bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-300 dark:border-blue-700 shadow-md'
        }
        animate-fadeInUp
      `}
      onClick={handleClick}
    >
      {/* Unread Indicator */}
      {!isRead && (
        <div className="absolute top-4 right-4 w-3 h-3 bg-blue-600 rounded-full animate-pulse" />
      )}

      <div className="flex items-start gap-4">
        {/* Icon */}
        <div
          className={`
            flex-shrink-0
            w-12 h-12 rounded-xl
            ${iconColor}
            flex items-center justify-center
            text-2xl shadow-lg
            transform group-hover:scale-110 transition-transform
          `}
        >
          {getNotificationIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3
              className={`
                font-bold text-sm leading-tight
                ${isRead ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}
              `}
            >
              {notification.title}
            </h3>
            {priority === 'urgent' && (
              <StatusBadge variant="error" size="sm">
                Urgent
              </StatusBadge>
            )}
          </div>

          <p
            className={`
              text-sm mb-2 line-clamp-2
              ${isRead ? 'text-gray-600 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}
            `}
          >
            {notification.message}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate(notification.createdAt)}
              </span>
              {notification.createdAt && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatTime(notification.createdAt)}
                  </span>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {!isRead && (
                <button
                  onClick={handleMarkRead}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  aria-label="Mark as read"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </button>
              )}
              <button
                onClick={handleDelete}
                className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                aria-label="Delete notification"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
